import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_BG_BRAND_WASH,
  BANK_BORDER,
  BANK_BORDER_SOFT,
  BANK_BORDER_STRONG,
  BANK_FS_BODY,
  BANK_FS_LEAD,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_OK,
  BANK_TEXT,
  BANK_TEXT_2,
  BANK_TEXT_BRAND,
  Button,
  GuardedButton,
  Input,
} from "@hakili/ui";
import { ArrowRight, Check, FileText, Send } from "lucide-react";
import { type ReactElement, type ReactNode, useState } from "react";

import { readReturnDay } from "../return-day.js";
import { DASH, fill } from "./dossiers.js";
import { GuardedAct } from "./guarded-act.js";
import type { FileOfferRead } from "./validation.js";
import { D2Kv as Kv, GUARD_CHIP, GUARD_FOOT } from "./d2-parts.js";

const rateLabel = (rate: string): string => rate.replace(".", ",");

/**
 * Round `T-1`, `M1` (`P2-BKW-004`; WF-BNK-15), then the DESIGN SPRINT (D-1026-2): **frame 45 of D2, verbatim**
 * (`analyst.js` `stageSignature`). The body is the drawing's two columns: « Dossier de signature » with its count
 * and the approval's chip, five rows (a 44 px tile, the title in bold, « parties · pages · note » under it, then the
 * « Signé » chip or « Marquer signé » taking the SCAN, a signature being a document with provenance), « Ce qui reste
 * après la signature »; beside them « Rendez-vous de signature » and « Conditions définitives » with the two fees on
 * two lines (I2). The foot is « Passer aux conditions préalables », guarded, and « Envoyer au client » until the send.
 * Nothing the drawing does not draw: no title line, no validity chip, no « Parties », no « Validité et délais », no
 * footnotes. The appointment's FIELD is held (the drawing's one free-text field against the door's typed agency and
 * moment: sprint rule 8), the card stays as the door reads and sets it.
 */
export interface SignatureDocumentRead {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly pageCount: number;
  readonly state: "pret" | "envoye" | "signe";
  readonly sentAt: string | null;
  readonly signedAt: string | null;
  readonly documentId: string | null;
}

/** `SIGNATURE-PLANNED`: one of the five names of the recorded pack, drawn before the send. */
export interface PlannedDocumentRead {
  readonly code: string;
  readonly label: string;
  readonly pageCount: number;
  readonly position: number;
}

export interface SignatureRead {
  /** The recorded pack, in pack order, the same five names before and after the send; carries no readiness of its own. */
  readonly plannedDocuments: readonly PlannedDocumentRead[];
  /** The single fact that the pack can be sent: the file is at the signature stage and the pack has not gone. */
  readonly sendable: boolean;
  readonly packSentAt: string | null;
  readonly contractSignedAt: string | null;
  readonly appointment: { readonly agence: string; readonly when: string } | null;
  readonly documents: readonly SignatureDocumentRead[];
  readonly signedCount: number;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const stateOf = (value: unknown): SignatureDocumentRead["state"] | null =>
  value === "pret" || value === "envoye" || value === "signe" ? value : null;

export function signatureFrom(answer: unknown): SignatureRead | null {
  const raw = (answer as { signature?: unknown } | null)?.signature;
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const appointment = value["appointment"] as Record<string, unknown> | null | undefined;
  const documents = Array.isArray(value["documents"])
    ? value["documents"].flatMap((one: unknown) => {
        const row = one as Record<string, unknown>;
        const state = stateOf(row["state"]);
        return typeof row["id"] === "string" && state !== null
          ? [
              {
                id: row["id"],
                code: str(row["code"]) ?? "",
                label: str(row["label"]) ?? "",
                pageCount: num(row["pageCount"]) ?? 0,
                state,
                sentAt: str(row["sentAt"]),
                signedAt: str(row["signedAt"]),
                documentId: str(row["documentId"]),
              },
            ]
          : [];
      })
    : [];
  const planned = Array.isArray(value["plannedDocuments"])
    ? value["plannedDocuments"].flatMap((one: unknown) => {
        const row = one as Record<string, unknown>;
        const code = str(row["code"]);
        const label = str(row["label"]);
        const pageCount = num(row["pageCount"]);
        const position = num(row["position"]);
        return code !== null && label !== null && pageCount !== null && position !== null
          ? [{ code, label, pageCount, position }]
          : [];
      })
    : [];
  return {
    plannedDocuments: planned,
    sendable: value["sendable"] === true,
    packSentAt: str(value["packSentAt"]),
    contractSignedAt: str(value["contractSignedAt"]),
    appointment:
      appointment !== null &&
      appointment !== undefined &&
      typeof appointment["agence"] === "string" &&
      typeof appointment["when"] === "string"
        ? { agence: appointment["agence"], when: appointment["when"] }
        : null,
    documents,
    signedCount: num(value["signedCount"]) ?? documents.filter((one) => one.state === "signe").length,
  };
}

/** The drawing's parties and notes per document, the screen's own words keyed by the row's code (wire values, 05 §2.6.21). */
const PARTIES = new Map<string, string>([
  ["contratPret", "bank.signature.partiesContrat"],
  ["acteGage", "bank.signature.partiesGage"],
  ["demandeDomiciliation", "bank.signature.partiesDomiciliation"],
  ["adhesionAssuranceEmprunteur", "bank.signature.partiesAssurance"],
  ["autorisationPrelevement", "bank.signature.partiesPrelevement"],
]);
const NOTES = new Map<string, string>([
  ["contratPret", "bank.signature.noteContrat"],
  ["acteGage", "bank.signature.noteGage"],
  ["demandeDomiciliation", "bank.signature.noteDomiciliation"],
  ["adhesionAssuranceEmprunteur", "bank.signature.noteAssurance"],
  ["autorisationPrelevement", "bank.signature.notePrelevement"],
]);

const pages = (n: number): string =>
  n === 1 ? t("bank.signature.unePage") : fill("bank.signature.pages", { n: String(n) });

/** The drawing's five titles, keyed by the row's code (wire values, 05 §2.6.21); the door's label is the fallback. */
const TITLES = new Map<string, string>([
  ["contratPret", "bank.signature.titreContrat"],
  ["acteGage", "bank.signature.titreGage"],
  ["demandeDomiciliation", "bank.signature.titreDomiciliation"],
  ["adhesionAssuranceEmprunteur", "bank.signature.titreAssurance"],
  ["autorisationPrelevement", "bank.signature.titrePrelevement"],
]);

const SIGNED_COUNT_KEYS = { one: "bank.signature.signeUn", many: "bank.signature.signes" } as const;

function Chip(props: {
  readonly children: ReactNode;
  readonly ok?: boolean;
  readonly testId?: string;
}): ReactElement {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[5px] ${BANK_FS_META} font-semibold leading-none ${
        props.ok === true ? BANK_OK : `bg-[color:var(--hk-neutral-bg)] ${BANK_TEXT}`
      }`}
      data-testid={props.testId}
    >
      {props.children}
    </span>
  );
}

function Card(props: {
  readonly title: string;
  readonly testId?: string;
  readonly sub?: ReactNode;
  readonly subTestId?: string;
  readonly right?: ReactNode;
  readonly flush?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section
      className={`mb-4 overflow-hidden rounded-[var(--hk-r-lg)] border bg-[color:var(--hk-surface)] ${BANK_BORDER}`}
      data-testid={props.testId}
    >
      <div className="flex items-center gap-3 px-5 pb-1 pt-4">
        <h3 className={`${BANK_FS_LEAD} font-semibold leading-[1.3] ${BANK_TEXT}`}>{props.title}</h3>
        {props.sub === undefined ? null : (
          <small className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid={props.subTestId}>
            {props.sub}
          </small>
        )}
        {props.right === undefined ? null : <span className="ml-auto">{props.right}</span>}
      </div>
      <div className={props.flush === true ? "pb-0 pt-3" : "px-5 pb-[18px] pt-3"}>{props.children}</div>
    </section>
  );
}

const SIGN_BUTTON = `inline-flex min-h-[34px] cursor-pointer items-center justify-center whitespace-nowrap rounded-[var(--hk-r-ctl)] border px-3 ${BANK_FS_BODY} font-semibold ${BANK_BORDER_STRONG} bg-[color:var(--hk-surface)] ${BANK_TEXT} focus-within:[outline:3px_solid_var(--hk-focus)] focus-within:outline-offset-2`;
const FOOT_BUTTON = `min-h-[48px] px-[22px] ${BANK_FS_READ}`;

export function SignaturePanel(props: {
  readonly read: FileOfferRead;
  readonly signature: SignatureRead;
  readonly clientName: string;
  readonly bankName: string;
  readonly busy: boolean;
  readonly onSendPack: () => void;
  readonly onSetAppointment: (agence: string, when: string) => void;
  readonly onMarkSigned: (documentId: string, scan: File) => void;
  readonly onProceed: () => void;
}): ReactElement {
  const { read, signature } = props;
  const offer = read.offer;
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [agence, setAgence] = useState("");
  const [jour, setJour] = useState("");
  const [heure, setHeure] = useState("");
  const sent = signature.packSentAt !== null;
  // `SIGNATURE-PLANNED`: before the send the door stores no rows, and says whether the recorded pack can go; the five names are then drawn
  // « Prêt à envoyer ». After the send the stored rows are the screen's.
  const rows: readonly SignatureDocumentRead[] =
    signature.documents.length > 0 || !signature.sendable
      ? signature.documents
      : signature.plannedDocuments.map((one) => ({
          id: `planned-${one.code}`,
          code: one.code,
          label: one.label,
          pageCount: one.pageCount,
          state: "pret" as const,
          sentAt: null,
          signedAt: null,
          documentId: null,
        }));
  const total = rows.length;
  const allSigned = sent && signature.signedCount === total && total > 0;
  // « 22 octobre » is read against THE DOOR'S OWN DAY (`asOf`, its clock in Bamako), never the browser's clock nor a date of the file's.
  const doorDay = read.asOf ?? null;
  const day = readReturnDay(jour, doorDay);
  // The typed hour is the agency's, Bamako's, the platform's one zone (UTC+0), as the catalogue prints it.
  const timeOk = /^([01]?\d|2[0-3]):[0-5]\d$/u.test(heure.trim());
  const reason =
    agence.trim() === ""
      ? t("bank.signature.rdvMotifAgence")
      : day === null
        ? t("bank.signature.rdvMotifJour")
        : !timeOk
          ? t("bank.signature.rdvMotifHeure")
          : null;
  const close = (): void => {
    setAppointmentOpen(false);
  };
  const count = (n: number): string =>
    fill(n > 1 ? SIGNED_COUNT_KEYS.many : SIGNED_COUNT_KEYS.one, { n: String(n), total: String(total) });
  return (
    <div data-testid="bank-signature">
      <div className="grid grid-cols-1 items-start gap-x-4 min-[1100px]:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div>
          <Card
            title={t("bank.signature.dossier")}
            flush
            {...(total > 0
              ? { sub: count(signature.signedCount), subTestId: "bank-signature-sous-titre" }
              : {})}
            right={
              <Chip ok testId="bank-signature-approbation">
                {read.requiresCommittee
                  ? t("bank.signature.approuveComite")
                  : t("bank.signature.approuveDelegation")}
              </Chip>
            }
          >
            {rows.length === 0 ? (
              <p className={`px-5 pb-4 ${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-signature-aucun">
                {t("bank.signature.aucunDocument")}
              </p>
            ) : (
              <ul>
                {rows.map((document) => (
                  <li
                    key={document.id}
                    className={`grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3.5 border-b px-5 py-3.5 last:border-b-0 ${BANK_BORDER_SOFT}`}
                    data-testid={`bank-signature-doc-${document.code}`}
                    data-state={document.state}
                  >
                    <span
                      className={`grid h-11 w-11 place-items-center rounded-xl ${
                        document.state === "signe" ? BANK_OK : `${BANK_BG_BRAND_WASH} ${BANK_TEXT_BRAND}`
                      }`}
                    >
                      {document.state === "signe" ? (
                        <Check size={22} strokeWidth={2.4} aria-hidden="true" />
                      ) : (
                        <FileText size={22} strokeWidth={1.7} aria-hidden="true" />
                      )}
                    </span>
                    <div className={`min-w-0 ${BANK_FS_READ}`}>
                      <b className={`block font-semibold ${BANK_TEXT}`}>
                        {TITLES.has(document.code) ? t(TITLES.get(document.code) ?? "") : document.label}
                      </b>
                      <small className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                        {t(PARTIES.get(document.code) ?? "bank.signature.partiesContrat")} ·{" "}
                        {pages(document.pageCount)} ·{" "}
                        {t(NOTES.get(document.code) ?? "bank.signature.noteContrat")}
                      </small>
                    </div>
                    {document.state === "signe" ? (
                      <Chip ok testId={`bank-signature-etat-${document.code}`}>
                        {t("bank.signature.etatSigne")}
                      </Chip>
                    ) : document.state === "pret" ? (
                      <Chip testId={`bank-signature-etat-${document.code}`}>
                        {t("bank.signature.etatPret")}
                      </Chip>
                    ) : (
                      // The scan IS the act: the file chosen goes to the documents' ticket, then the door.
                      <label className={SIGN_BUTTON} data-testid={`bank-signature-marquer-${document.code}`}>
                        {t("bank.signature.marquerSigne")}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          className="sr-only"
                          disabled={props.busy}
                          aria-label={`${t("bank.signature.marquerSigne")} : ${document.label}`}
                          data-testid={`bank-signature-scan-${document.code}`}
                          onChange={(event) => {
                            const scan = event.target.files?.[0];
                            if (scan !== undefined) props.onMarkSigned(document.id, scan);
                          }}
                        />
                      </label>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title={t("bank.signature.reste")} testId="bank-signature-reste">
            <p className={`mb-2 ${BANK_FS_BODY} ${BANK_TEXT}`}>{t("bank.signature.resteIntro")}</p>
            <ul className={`mt-1.5 ml-[18px] grid list-disc gap-1 pl-5 ${BANK_FS_READ} ${BANK_TEXT}`}>
              {(
                [
                  "bank.signature.resteDomiciliation",
                  "bank.signature.resteAssurance",
                  "bank.signature.resteGage",
                ] as const
              ).map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </Card>
        </div>

        <div>
          <Card title={t("bank.signature.rdv")} testId="bank-signature-rdv">
            {signature.appointment === null ? (
              <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-signature-rdv-ligne">
                {t("bank.signature.rdvAucun")}
              </p>
            ) : (
              <p
                className={`${BANK_FS_LEAD} font-semibold ${BANK_TEXT}`}
                data-testid="bank-signature-rdv-ligne"
              >
                {fill("bank.signature.rdvLigne", {
                  when: formatDateTime(new Date(signature.appointment.when)),
                  agence: signature.appointment.agence,
                })}
              </p>
            )}
            {appointmentOpen ? (
              <div data-testid="bank-signature-rdv-form">
                <div className="mt-3">
                  <label
                    htmlFor="bank-signature-rdv-agence"
                    className={`mb-1 block ${BANK_FS_BODY} font-semibold ${BANK_TEXT}`}
                  >
                    {t("bank.signature.rdvAgence")}
                  </label>
                  <Input
                    id="bank-signature-rdv-agence"
                    value={agence}
                    onChange={setAgence}
                    placeholder={t("bank.signature.rdvAgenceExemple")}
                    autoComplete="off"
                    testId="bank-signature-rdv-agence"
                  />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-4 min-[1100px]:grid-cols-2">
                  <div>
                    <label
                      htmlFor="bank-signature-rdv-jour"
                      className={`mb-1 block ${BANK_FS_BODY} font-semibold ${BANK_TEXT}`}
                    >
                      {t("bank.signature.rdvJour")}
                    </label>
                    <Input
                      id="bank-signature-rdv-jour"
                      value={jour}
                      onChange={setJour}
                      placeholder={t("bank.signature.rdvJourExemple")}
                      autoComplete="off"
                      testId="bank-signature-rdv-jour"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="bank-signature-rdv-heure"
                      className={`mb-1 block ${BANK_FS_BODY} font-semibold ${BANK_TEXT}`}
                    >
                      {t("bank.signature.rdvHeure")}
                    </label>
                    <Input
                      id="bank-signature-rdv-heure"
                      value={heure}
                      onChange={setHeure}
                      placeholder={t("bank.signature.rdvHeureExemple")}
                      autoComplete="off"
                      testId="bank-signature-rdv-heure"
                    />
                  </div>
                </div>
                <div className="mt-3.5 flex flex-wrap items-center justify-start gap-3">
                  <Button variant="ghost" size="sm" onClick={close} data-testid="bank-signature-rdv-annuler">
                    {t("bank.signature.rdvAnnuler")}
                  </Button>
                  {reason !== null || day === null ? (
                    <GuardedAct
                      small
                      wrap
                      testId="bank-signature-rdv-confirmer"
                      reasonId="bank-signature-rdv-raison"
                      reason={reason}
                    >
                      {t("bank.signature.rdvConfirmer")}
                    </GuardedAct>
                  ) : (
                    <Button
                      variant="ink"
                      size="sm"
                      disabled={props.busy}
                      onClick={() => {
                        props.onSetAppointment(
                          agence.trim(),
                          new Date(`${day}T${heure.trim().padStart(5, "0")}:00Z`).toISOString(),
                        );
                        close();
                      }}
                      data-testid="bank-signature-rdv-confirmer"
                    >
                      {t("bank.signature.rdvConfirmer")}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-2.5">
                <Button
                  variant="secondary"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => {
                    setAgence("");
                    setJour("");
                    setHeure("");
                    setAppointmentOpen(true);
                  }}
                  data-testid="bank-signature-rdv-ouvrir"
                >
                  {signature.appointment === null
                    ? t("bank.signature.rdvFixer")
                    : t("bank.signature.rdvReporter")}
                </Button>
              </div>
            )}
          </Card>

          <Card title={t("bank.signature.conditionsDefinitives")} testId="bank-signature-conditions">
            <dl>
              <Kv label={t("bank.signature.montant")} value={formatMoney(read.facts.amountFinanced)} />
              <Kv
                plain
                label={t("bank.signature.duree")}
                value={
                  offer === null
                    ? DASH
                    : fill("bank.dossier.dureeMois", { duree: String(offer.durationMonths) })
                }
              />
              <Kv
                label={t("bank.offre.tauxNominal")}
                value={
                  offer === null ? DASH : fill("bank.decision.pct", { n: rateLabel(offer.nominalRatePct) })
                }
              />
              <Kv
                label={t("bank.signature.mensualite")}
                value={offer === null ? DASH : formatMoney(offer.recap.instalmentAmount)}
              />
              <Kv
                label={t("bank.offre.taeg")}
                value={
                  offer === null ? DASH : fill("bank.decision.pct", { n: rateLabel(offer.recap.taegPct) })
                }
              />
              {/* I2 and WF-BNK-15's acceptance: the two file fees on two lines, never merged. */}
              <Kv
                label={t("bank.signature.fraisBanque")}
                value={offer === null ? DASH : formatMoney(offer.recap.bankFeeAmount)}
                testId="bank-signature-frais-banque"
              />
              <Kv
                label={t("bank.signature.fraisHakili")}
                value={offer === null ? DASH : formatMoney(offer.recap.hakiliFeeAmount)}
                testId="bank-signature-frais-hakili"
              />
            </dl>
          </Card>
        </div>
      </div>

      <ActionBar label={t("bank.signature.barreActions")}>
        {!allSigned ? (
          // WF-BNK-15's guard: disabled while any document is unsigned, with the drawing's sentence beside it.
          <GuardedButton
            disabled
            {...GUARD_CHIP}
            reason={!sent ? t("bank.signature.piedEnvoyer") : t("bank.signature.piedAttente")}
            className={GUARD_FOOT}
            testId="bank-signature-conditions-passer"
          >
            <span className="inline-flex items-center gap-2">
              {t("bank.signature.passerConditions")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </GuardedButton>
        ) : (
          <Button
            className={`inline-flex items-center gap-2 whitespace-nowrap ${FOOT_BUTTON}`}
            disabled={props.busy}
            onClick={props.onProceed}
            data-testid="bank-signature-conditions-passer"
          >
            {t("bank.signature.passerConditions")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        {/* D2 `.sp{flex:1}`: the spacer, not an auto margin; with a long reason the button wraps to the left of a second line. */}
        <span className="flex-1" aria-hidden="true" />
        {sent ? null : (
          <Button
            variant="ink"
            className={`inline-flex items-center gap-2 whitespace-nowrap ${FOOT_BUTTON}`}
            disabled={props.busy}
            onClick={props.onSendPack}
            data-testid="bank-signature-envoyer"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {t("bank.signature.envoyer")}
          </Button>
        )}
      </ActionBar>
    </div>
  );
}
